// SdsTab.js
// DEL form Misc > SDS sub-tab: the SDS attached to this DEL plus the shared 3E catalog search
// (PhotosTab adapter idiom: thin wrapper over a shared feature component).

import React, { useEffect, useState } from "react";
import { useUserState } from "../../../../context/user";
import { Button } from "../../../sdsLookup/ui";
import ConfirmationModal from "../../../common/ConfirmationModal";
import { SdsLookupSection } from "../../../sdsLookup/sdsLookupSection/SdsLookupSection";
import { getSdsStatus, buildSdsDownloadUrl } from "../../../../api/SdsApi";
import styles from "./SdsTab.module.css";

// One attached SDS: snapshot fields plus View / Download anchors and an optional Remove
const SdsAttachedRow = ({ reference, canEdit, onRemove }) => {
  const json = reference.toJson();
  return (
    <li className={styles.attachedRow} data-testid="sds-attached-row">
      <div className={styles.attachedInfo}>
        <div className={styles.attachedTitle}>
          {reference.productName || "Unnamed product"}
        </div>
        <div className={styles.attachedMeta}>
          {reference.manufacturerName && <span>{reference.manufacturerName}</span>}
          {reference.manufacturerPartNumber && (
            <span>Part {reference.manufacturerPartNumber}</span>
          )}
          {reference.revisionDate && <span>revised {reference.revisionDate}</span>}
        </div>
      </div>
      <div className={styles.attachedActions}>
        {reference.documentLink && (
          <>
            <a
              className={styles.primaryLink}
              href={reference.documentLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              View SDS
            </a>
            <a
              className={styles.secondaryLink}
              href={buildSdsDownloadUrl(json)}
              download
              target="_blank"
              rel="noopener noreferrer"
            >
              Download
            </a>
          </>
        )}
        {canEdit && (
          <Button
            type="button"
            variant="danger"
            onClick={() => onRemove(reference.rowId)}
            aria-label={`Remove ${reference.productName || "SDS"}`}
          >
            Remove
          </Button>
        )}
      </div>
    </li>
  );
};

/**
 * @param {object} callbacks - { delData, delMode, emitPatch, connectionStatus }
 * @param {object} sdsActions - makeSdsReferencesActions (attach / remove / handleBroadcast)
 * @param {function} showNotification - notificationActions.showNotification
 */
export const SdsTab = ({ callbacks, sdsActions, showNotification }) => {
  const { currentUser } = useUserState();
  const [sdsStatus, setSdsStatus] = useState(null);
  const [pendingRemoveId, setPendingRemoveId] = useState(null);

  const isViewMode = callbacks.delMode === "view";
  const isOnline = callbacks.connectionStatus === "connected";
  const canEdit = !isViewMode && typeof callbacks.emitPatch === "function";
  const references = callbacks.delData?.sdsReferences || [];
  const attachedProductUids = references
    .map((reference) => reference.productUid)
    .filter(Boolean);

  // Feature flags are only needed for the search; fetch once we can edit and reach the server
  useEffect(() => {
    if (!canEdit || !isOnline || sdsStatus) return;
    let cancelled = false;
    getSdsStatus()
      .then((data) => {
        if (!cancelled && data) setSdsStatus(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [canEdit, isOnline, sdsStatus]);

  const handleAttach = (hit) => {
    const result = sdsActions.attachSdsReference(
      hit,
      currentUser?.employee_id,
      callbacks.emitPatch
    );
    if (result?.duplicate) {
      showNotification?.({
        type: "warning",
        title: "Already attached",
        message: `${hit?.product_name || "This SDS"} is already attached to this DEL.`,
      });
    }
    return result;
  };

  const handleConfirmRemove = () => {
    if (pendingRemoveId) {
      sdsActions.removeSdsReference(pendingRemoveId, callbacks.emitPatch);
    }
    setPendingRemoveId(null);
  };

  const handleCancelRemove = () => setPendingRemoveId(null);

  return (
    <div className={styles.sdsTabContainer} data-testid="sds-tab">
      <div className={styles.attachedSection}>
        <h3 className={styles.sectionTitle}>Attached SDS</h3>
        {references.length === 0 ? (
          <p className={styles.emptyText}>No SDS attached to this DEL.</p>
        ) : (
          <ul className={styles.attachedList}>
            {references.map((reference) => (
              <SdsAttachedRow
                key={reference.rowId}
                reference={reference}
                canEdit={canEdit}
                onRemove={setPendingRemoveId}
              />
            ))}
          </ul>
        )}
      </div>

      <ConfirmationModal
        isOpen={!!pendingRemoveId}
        title="Remove SDS"
        message="Remove this SDS from the DEL? The SDS document itself is not deleted."
        confirmText="Remove"
        cancelText="Cancel"
        confirmButtonClass="deleteButton"
        onConfirm={handleConfirmRemove}
        onCancel={handleCancelRemove}
      />

      {canEdit && (
        <div className={styles.searchSection}>
          <h3 className={styles.sectionTitle}>Find an SDS</h3>
          <SdsLookupSection
            isOnline={isOnline}
            sdsStatus={sdsStatus}
            onAttach={handleAttach}
            attachedProductUids={attachedProductUids}
            delId={callbacks.delData?.id}
            showNotification={showNotification}
          />
        </div>
      )}
    </div>
  );
};

export default SdsTab;
